import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, Clock, Users, Award, MessageSquare } from "lucide-react";
import { useState } from "react";

export function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    partnerType: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log("Form submitted:", formData);
  };

  const partnerTypes = [
    "Food Business Operator (FBO)",
    "Biofuel Manufacturer",
    "Policy Maker / Regulator",
    "Investor / Partner",
    "Other",
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: "Visit Us",
      details: ["331/6, 1st Floor, Valayapathy Salai", "Mogappair East, Chennai – 600 037"],
      gradient: "from-[#7CB342] to-[#689F38]",
    },
    {
      icon: Phone,
      title: "Call Us",
      details: ["+91 8056801422", "Mon-Sat: 9:00 AM - 6:00 PM"],
      gradient: "from-[#1976D2] to-[#1565C0]",
    },
    {
      icon: Mail,
      title: "Email Us",
      details: ["info@krbcleanenergy.com", "Quick Response Guaranteed"],
      gradient: "from-purple-500 to-indigo-600",
    },
  ];

  const quickLinks = [
    { icon: Users, text: "Partner Onboarding", gradient: "from-[#7CB342] to-[#689F38]" },
    { icon: Award, text: "ISCC Certification Info", gradient: "from-[#1976D2] to-[#1565C0]" },
    { icon: MessageSquare, text: "Technical Support", gradient: "from-purple-500 to-indigo-600" },
    { icon: Clock, text: "Schedule a Demo", gradient: "from-orange-500 to-red-600" },
  ];

  return (
    <section id="contact" className="relative py-20 lg:py-32 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#7CB342]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#1976D2]/5 rounded-full blur-3xl" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.02]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="contactGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#contactGrid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342]/10 to-[#1976D2]/10 border-2 border-[#7CB342]/30 rounded-full px-5 py-2.5 mb-6"
          >
            <MessageSquare className="w-5 h-5 text-[#7CB342]" />
            <span className="text-sm font-bold text-gray-900">Get In Touch</span>
          </motion.div>

          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Let's Transform{" "}
            <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
              Waste Into Power
            </span>
          </h2>

          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Partner with <span className="font-bold text-gray-900">KRB Clean Energy Private Limited</span> and join India's clean energy revolution
          </p>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16"
        >
          {quickLinks.map((link, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative"
            >
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${link.gradient} rounded-xl opacity-0 group-hover:opacity-50 blur transition-all`} />
              <div className="relative bg-white border-2 border-gray-200 group-hover:border-transparent rounded-xl p-4 lg:p-6 transition-all">
                <link.icon className={`w-6 h-6 lg:w-8 lg:h-8 mx-auto mb-2 bg-gradient-to-r ${link.gradient} bg-clip-text text-transparent group-hover:scale-110 transition-transform`} />
                <p className="text-xs lg:text-sm font-bold text-gray-900">{link.text}</p>
              </div>
            </motion.button>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-3xl opacity-20 blur-2xl" />
              
              {/* Form Card */}
              <div className="relative bg-white rounded-3xl p-8 lg:p-10 shadow-2xl border-2 border-gray-200">
                <div className="mb-8">
                  <h3 className="text-2xl lg:text-3xl font-black text-gray-900 mb-2">
                    Start Your Partnership Journey
                  </h3>
                  <p className="text-gray-600">
                    Fill out the form and our team will connect with you within 24 hours
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#7CB342] focus:outline-none transition-colors text-gray-900"
                      placeholder="Enter your full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#7CB342] focus:outline-none transition-colors text-gray-900"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#7CB342] focus:outline-none transition-colors text-gray-900"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Company / Organization Name
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#7CB342] focus:outline-none transition-colors text-gray-900"
                      placeholder="Your company name"
                    />
                  </div>

                  {/* Partner Type */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      I am a *
                    </label>
                    <select
                      required
                      value={formData.partnerType}
                      onChange={(e) => setFormData({ ...formData, partnerType: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#7CB342] focus:outline-none transition-colors text-gray-900"
                    >
                      <option value="">Select partner type</option>
                      {partnerTypes.map((type, index) => (
                        <option key={index} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#7CB342] focus:outline-none transition-colors resize-none text-gray-900"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="group w-full bg-gradient-to-r from-[#7CB342] to-[#1976D2] text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-[#7CB342]/30 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    Send Message
                    <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <p className="text-xs text-gray-600 text-center">
                    By submitting this form, you agree to be contacted by{" "}
                    <span className="font-bold">KRB Clean Energy Private Limited</span>
                  </p>
                </form>
              </div>
            </div>
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Contact Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${info.gradient} rounded-2xl opacity-0 group-hover:opacity-50 blur transition-all`} />
                  <div className="relative bg-white border-2 border-gray-200 group-hover:border-transparent rounded-2xl p-6 transition-all">
                    <div className="flex items-start gap-4">
                      <div className={`bg-gradient-to-br ${info.gradient} rounded-xl p-3 group-hover:scale-110 transition-transform`}>
                        <info.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-gray-900 mb-2">{info.title}</h4>
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-gray-600 text-sm">
                            {detail}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Company Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] via-[#1976D2] to-[#7CB342] rounded-3xl opacity-30 blur-xl" />
              <div className="relative bg-gradient-to-br from-[#7CB342] to-[#1976D2] rounded-3xl p-8 lg:p-10 text-white">
                <h3 className="text-2xl font-black mb-4">
                  KRB Clean Energy Private Limited
                </h3>
                <p className="text-white/90 mb-6 leading-relaxed">
                  India's leading Used Cooking Oil aggregator, transforming waste into certified renewable fuel feedstock through IoT-enabled digital traceability.
                </p>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/30">
                  {[
                    { value: "100%", label: "Traceable" },
                    { value: "ISCC", label: "Certified" },
                    { value: "24/7", label: "Support" },
                  ].map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-2xl font-black text-white mb-1">{stat.value}</div>
                      <div className="text-xs text-white/80 font-semibold">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-white/30">
                  <p className="text-center text-lg font-bold text-white">
                    Where Waste Becomes Power
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}